import React from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { DollarSign, TrendingUp, Shield, Users } from 'lucide-react';

const PRODUCTS = {
  insurance: {
    label: 'Insurance Products',
    icon: Shield,
    color: 'blue',
    items: [
      'Life Insurance',
      'Disability Insurance',
      'Health & Dental Insurance',
      'Travel Insurance and Super Visa',
      'Critical Illness Insurance',
      'Accidental Death & Dismemberment',
      'Business Overhead Expense Insurance',
      'Key Person Insurance',
      'Monthly & Annual Travel Medical Emergency',
      'Truck Driver Insurance'
    ]
  },
  investment: {
    label: 'Investment Products',
    icon: TrendingUp,
    color: 'purple',
    items: [
      'RRSP',
      'TFSA/FHSA',
      'RESP',
      'RDSP',
      'Non-Registered Account',
      'Business Account',
      'Mutual Funds and Segregated Funds',
      'Group Plans',
      'LIRA/LIF/RRIF',
      'RRSP Loan'
    ]
  },
  referral: {
    label: 'Referral Products',
    icon: Users,
    color: 'green',
    items: [
      'SUREX Insurance - Home',
      'SUREX Insurance - Auto',
      'SUREX Insurance - Condo',
      'SUREX Insurance - Tenant',
      'SUREX Insurance - Motorcycle/ATV',
      'SUREX Insurance - Snowmobile',
      'SUREX Insurance - Landlord',
      'SUREX Insurance - Business',
      'SUREX Insurance - Boat',
      'SUREX Insurance - Farm',
      'SUREX Insurance - Trailer/RV',
      '8Twelve Mortgage',
      'Debt Consolidation',
      'PPL (Pre-Paid Legal Services)',
      'ID Theft Defense'
    ]
  }
};

export const ProductsTrackerTable = ({ productsSold, onProductsChange, investmentDollarAmount, onInvestmentDollarAmountChange }) => {
  const handleProductChange = (product, value) => {
    const newProducts = { ...productsSold, [product]: parseFloat(value) || 0 };
    onProductsChange(newProducts);
  };

  const getTotalByCategory = (category) => {
    return PRODUCTS[category].items.reduce((sum, product) => {
      return sum + (productsSold[product] || 0);
    }, 0);
  };

  const getGrandTotal = () => {
    let total = 0;
    Object.keys(PRODUCTS).forEach(category => {
      total += getTotalByCategory(category);
    });
    return total;
  };

  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const CategoryTable = ({ categoryKey }) => {
    const category = PRODUCTS[categoryKey];
    const Icon = category.icon;
    const colorClasses = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', header: 'bg-blue-100' },
      purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', header: 'bg-purple-100' },
      green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', header: 'bg-green-100' }
    };
    const colors = colorClasses[category.color];
    const categoryTotal = getTotalByCategory(categoryKey);

    return (
      <div className={`rounded-lg border ${colors.border} overflow-hidden`}>
        <div className={`${colors.header} px-4 py-3 flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${colors.text}`} />
            <h3 className="font-semibold text-[#333333]">{category.label}</h3>
          </div>
          <Badge className={`${colors.text} bg-white`}>
            ${formatCurrency(categoryTotal)}
          </Badge>
        </div>
        <table className="w-full" data-testid={`products-table-${categoryKey}`}>
          <thead>
            <tr className={`${colors.bg} text-sm`}>
              <th className="text-left px-4 py-2 font-medium text-gray-600">Product</th>
              <th className="text-right px-4 py-2 font-medium text-gray-600 w-36">Amount ($)</th>
            </tr>
          </thead>
          <tbody>
            {category.items.map((product, idx) => (
              <tr 
                key={product} 
                className={`border-t ${colors.border} ${idx % 2 === 0 ? 'bg-white' : colors.bg}`}
              >
                <td className="px-4 py-2 text-sm text-gray-700">{product}</td>
                <td className="px-4 py-2">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={productsSold[product] || ''}
                      onChange={(e) => handleProductChange(product, e.target.value)}
                      className="pl-6 h-8 text-sm text-right"
                      placeholder="0.00"
                      data-testid={`product-input-${product.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Card className="p-6" data-testid="products-tracker-table">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6 text-[#E31837]" />
          <h2 className="text-xl font-semibold text-[#333333]">Products & Investment Tracker</h2>
        </div>
        <div className="flex items-center gap-4">
          <Badge className="bg-[#E31837] text-white text-lg px-4 py-2">
            Grand Total: ${formatCurrency(getGrandTotal() + (investmentDollarAmount || 0))}
          </Badge>
        </div>
      </div>

      {/* Investment Business Dollar Amount - Dedicated Section */}
      <div className="mb-6 p-4 bg-gradient-to-r from-[#E31837]/10 to-[#E31837]/5 rounded-lg border-2 border-[#E31837]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-lg text-[#333333]">Investment Business (Total Dollar Amount)</h3>
            <p className="text-sm text-gray-600 mt-1">Enter the total dollar amount of investment business closed today</p>
          </div>
          <div className="relative w-full sm:w-48">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={investmentDollarAmount || ''}
              onChange={(e) => onInvestmentDollarAmountChange(parseFloat(e.target.value) || 0)}
              className="pl-8 h-12 text-lg font-semibold text-right border-2 border-[#E31837]"
              placeholder="0.00"
              data-testid="investment-dollar-amount-input"
            />
          </div>
        </div>
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
          <Shield className="h-6 w-6 text-blue-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Insurance</p>
          <p className="text-2xl font-bold text-blue-600" data-testid="insurance-total">
            ${formatCurrency(getTotalByCategory('insurance'))}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
          <TrendingUp className="h-6 w-6 text-purple-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Investment Products</p>
          <p className="text-2xl font-bold text-purple-600" data-testid="investment-total">
            ${formatCurrency(getTotalByCategory('investment'))}
          </p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
          <Users className="h-6 w-6 text-green-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Referrals</p>
          <p className="text-2xl font-bold text-green-600" data-testid="referral-total">
            ${formatCurrency(getTotalByCategory('referral'))}
          </p>
        </div>
      </div>

      {/* Product Tables */}
      <div className="space-y-6">
        <CategoryTable categoryKey="insurance" />
        <CategoryTable categoryKey="investment" />
        <CategoryTable categoryKey="referral" />
      </div>

      {/* Grand Total Footer */}
      <div className="mt-6 p-4 bg-gradient-to-r from-gray-100 to-gray-50 rounded-lg border-2 border-gray-300">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-xs text-gray-500 uppercase">Insurance</p>
            <p className="text-lg font-bold text-blue-600">${formatCurrency(getTotalByCategory('insurance'))}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Investment Products</p>
            <p className="text-lg font-bold text-purple-600">${formatCurrency(getTotalByCategory('investment'))}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase">Referrals</p>
            <p className="text-lg font-bold text-green-600">${formatCurrency(getTotalByCategory('referral'))}</p>
          </div>
          <div className="border-l-2 border-[#E31837]">
            <p className="text-xs text-gray-500 uppercase">Investment Business</p>
            <p className="text-lg font-bold text-[#E31837]">${formatCurrency(investmentDollarAmount || 0)}</p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t-2 border-gray-300 text-center">
          <p className="text-sm text-gray-600 mb-1">Total Business Value Today</p>
          <p className="text-3xl font-bold text-[#E31837]" data-testid="grand-total">
            ${formatCurrency(getGrandTotal() + (investmentDollarAmount || 0))}
          </p>
        </div>
      </div>
    </Card>
  );
};
